import {
  HISTORY_COVERAGE_MODE,
  reconcileHistoryOnce,
  selectHeadCoverageFloor
} from "./reconnect-history";

const DEFAULT_RETRY_DELAYS = [0, 500, 1500];
const DEFAULT_MAX_PAGES = 100;
const DEFAULT_MAX_CHUNK_DURATION_MS = 15 * 1000;
const DEFAULT_MAX_BURST_PAGES = 500;
const DEFAULT_HEAD_PROBE_WINDOW_MESSAGES = 500;

export const HISTORY_RECONCILE_REASON = Object.freeze({
  AUTHENTICATED: "authenticated",
  DELAYED: "delayed",
  FOCUS: "focus",
  GAP: "gap",
  INITIAL_HISTORY: "initial-history",
  ONLINE: "online",
  PAGESHOW: "pageshow",
  POLL: "poll",
  RECONNECT: "reconnect",
  SCOPE: "scope",
  VERIFY: "verify",
  VISIBLE: "visible"
});

export const HISTORY_RECONCILE_TIMING = Object.freeze({
  POLL_BASE_MS: 30 * 1000,
  POLL_JITTER_RATIO: 0.2,
  // Long backward scans periodically probe beginTime=0 so a recent missed
  // push is not hidden behind an old continuation cursor.
  HEAD_PROBE_INTERVAL_MS: 10 * 1000,
  HEAD_PROBE_WINDOW_MESSAGES: DEFAULT_HEAD_PROBE_WINDOW_MESSAGES,
  HEAD_PROBE_MAX_PAGES: DEFAULT_MAX_PAGES,
  DELAYED_SECOND_MS: 5 * 1000,
  // Ordinary history restores scroll after 100 ms; wait beyond that before a
  // queued reconciliation is allowed to update the message list.
  HISTORY_SETTLE_MS: 250,
  CONTINUATION_DELAY_MS: 25,
  CONTINUATION_BACKOFF_MS: 5 * 1000,
  FAILURE_BACKOFF_MS: [5 * 1000, 15 * 1000, 30 * 1000, 60 * 1000]
});

const wait = delay =>
  new Promise(resolve => {
    setTimeout(resolve, delay);
  });

const hasValue = value => value !== undefined && value !== null && value !== "";

const toFiniteNumber = value => {
  if (!hasValue(value)) return undefined;
  const number = Number(value);
  return Number.isFinite(number) ? number : undefined;
};

const getMessagesForUser = (state, userId) => {
  const messageMap = state && state.chat && state.chat.messageMap;
  const messages = messageMap && messageMap[userId];
  return Array.isArray(messages) ? messages : [];
};

const getHistoryGeneration = state => {
  const generation = toFiniteNumber(
    state && state.ws && state.ws.historyGeneration
  );
  return generation === undefined ? 0 : generation;
};

const createCoverageCandidateIdentity = messages =>
  JSON.stringify(
    (Array.isArray(messages) ? messages : [])
      .map(message =>
        JSON.stringify([
          hasValue(message && message.msgId) ? String(message.msgId) : "",
          hasValue(message && message.channelId)
            ? String(message.channelId)
            : "",
          toFiniteNumber(message && message.sort)
        ])
      )
      .sort()
  );

const selectOlderCoverageCandidate = (previousMessages, nextMessages) => {
  const previous = Array.isArray(previousMessages) ? previousMessages : [];
  const next = Array.isArray(nextMessages) ? nextMessages : [];
  const minSort = messages =>
    messages.reduce((minimum, message) => {
      const sort = toFiniteNumber(message && message.sort);
      if (sort === undefined || sort <= 0) return minimum;
      return minimum === undefined || sort < minimum ? sort : minimum;
    }, undefined);
  const previousMinSort = minSort(previous);
  const nextMinSort = minSort(next);
  if (previousMinSort === undefined) return next;
  if (nextMinSort === undefined || nextMinSort >= previousMinSort) {
    return previous;
  }
  return next;
};

const REASON_PRIORITY = [
  HISTORY_RECONCILE_REASON.RECONNECT,
  HISTORY_RECONCILE_REASON.ONLINE,
  HISTORY_RECONCILE_REASON.PAGESHOW,
  HISTORY_RECONCILE_REASON.VISIBLE,
  HISTORY_RECONCILE_REASON.FOCUS,
  HISTORY_RECONCILE_REASON.GAP,
  HISTORY_RECONCILE_REASON.SCOPE,
  HISTORY_RECONCILE_REASON.INITIAL_HISTORY,
  HISTORY_RECONCILE_REASON.AUTHENTICATED,
  HISTORY_RECONCILE_REASON.VERIFY,
  HISTORY_RECONCILE_REASON.POLL,
  HISTORY_RECONCILE_REASON.DELAYED
];

const chooseReason = (currentReason, nextReason) => {
  if (!currentReason) return nextReason;
  const currentPriority = REASON_PRIORITY.indexOf(currentReason);
  const nextPriority = REASON_PRIORITY.indexOf(nextReason);
  if (currentPriority === -1) return nextReason;
  if (nextPriority === -1) return currentReason;
  return nextPriority < currentPriority ? nextReason : currentReason;
};

const isResumeReason = reason =>
  [
    HISTORY_RECONCILE_REASON.RECONNECT,
    HISTORY_RECONCILE_REASON.ONLINE,
    HISTORY_RECONCILE_REASON.PAGESHOW,
    HISTORY_RECONCILE_REASON.VISIBLE,
    HISTORY_RECONCILE_REASON.FOCUS,
    HISTORY_RECONCILE_REASON.GAP,
    HISTORY_RECONCILE_REASON.SCOPE
  ].includes(reason);

const isImmediateHeadProbeReason = reason =>
  [
    HISTORY_RECONCILE_REASON.RECONNECT,
    HISTORY_RECONCILE_REASON.ONLINE,
    HISTORY_RECONCILE_REASON.PAGESHOW,
    HISTORY_RECONCILE_REASON.VISIBLE,
    HISTORY_RECONCILE_REASON.FOCUS,
    HISTORY_RECONCILE_REASON.POLL,
    HISTORY_RECONCILE_REASON.DELAYED
  ].includes(reason);

/**
 * Owns every reconciliation trigger and timer. Invariants:
 * - one request chain is active at a time;
 * - an active chain keeps at most one trailing run;
 * - each run captures one monotonic generation and one fixed coverage floor;
 * - ordinary history pagination and scroll state are never touched.
 */
export const createHistoryReconcileManager = ({
  timing = HISTORY_RECONCILE_TIMING,
  retryDelays = DEFAULT_RETRY_DELAYS,
  waitForRetry = wait,
  maxPages = DEFAULT_MAX_PAGES,
  maxDurationMs = DEFAULT_MAX_CHUNK_DURATION_MS,
  maxBurstPages = DEFAULT_MAX_BURST_PAGES,
  random = Math.random,
  now = () => Date.now(),
  setTimer = (handler, delay) => setTimeout(handler, delay),
  clearTimer = timer => clearTimeout(timer),
  windowObject = typeof window === "undefined" ? undefined : window,
  documentObject = typeof document === "undefined" ? undefined : document,
  navigatorObject = typeof navigator === "undefined" ? undefined : navigator
} = {}) => {
  let store;
  let router;
  let getHistoryMessage;
  let createRequestCancellation;
  let diagnostics;
  let started = false;
  let paused = false;
  let runtimeAuthenticated = false;
  let pageActive = true;
  let generation = 0;
  let lastScope;
  let cycleTimer;
  let delayedTimer;
  let continuationCooldownTimer;
  let continuationCoolingDown = false;
  let continuationPendingReason;
  let failureCoolingDown = false;
  let failurePendingReason;
  let activeRun;
  let failureIndex = 0;
  let pendingReason;
  let workEpoch = 0;
  let unsubscribeStore;
  let removeRouterHook;
  let lifecycleVm;
  let lifecycleDestroyHandler;
  let coverageIdentity;
  let coverageFloorMessages;
  let coverageMode;
  let provisionalCandidateFloorMessages;
  let provisionalCandidateIdentity;
  let provisionalCandidateObservedAt;
  let coverageExpansionPending = false;
  let scanContinuation;
  let scanContinuationIdentity;
  let freshVerificationPending = false;
  let listenersAttached = false;

  const clearCycleTimer = () => {
    if (cycleTimer !== undefined) clearTimer(cycleTimer);
    cycleTimer = undefined;
  };

  const clearDelayedTimer = () => {
    if (delayedTimer !== undefined) clearTimer(delayedTimer);
    delayedTimer = undefined;
  };

  const clearContinuationCooldown = () => {
    if (continuationCooldownTimer !== undefined) {
      clearTimer(continuationCooldownTimer);
    }
    continuationCooldownTimer = undefined;
    continuationCoolingDown = false;
    continuationPendingReason = undefined;
  };

  const clearFailureCooldown = () => {
    failureCoolingDown = false;
    failurePendingReason = undefined;
  };

  const readRouteName = () => {
    const route = router && router.currentRoute;
    return route && route.name ? route.name : "";
  };

  const isVisible = () =>
    !documentObject ||
    (documentObject.hidden !== true &&
      documentObject.visibilityState !== "hidden");

  const isOnline = () => !navigatorObject || navigatorObject.onLine !== false;

  const readScope = () => {
    const state = (store && store.state) || {};
    const wsState = state.ws || {};
    const scope = {
      userId: hasValue(state.userId) ? String(state.userId) : "",
      channelId: hasValue(state.channelId) ? String(state.channelId) : "",
      conversation: state.isConversation === true,
      conversationEnd: state.isConversationEnd === true,
      queue: state.isQueue === true,
      historyLoading: state.hasHistoryLoad === true,
      socketAuthenticated:
        runtimeAuthenticated && state.socketConnectSuccess === true,
      // This value participates only in the private scope fingerprint. It is
      // never emitted through diagnostics.
      sessionToken: hasValue(state.token) ? String(state.token) : "",
      routeName: readRouteName(),
      visible: isVisible(),
      online: isOnline(),
      pageActive,
      paused,
      reconnecting: wsState.isReconnect === true
    };
    scope.eligible = Boolean(
      started &&
        !scope.paused &&
        scope.socketAuthenticated &&
        scope.conversation &&
        !scope.conversationEnd &&
        !scope.queue &&
        !scope.historyLoading &&
        scope.userId &&
        scope.channelId &&
        Number(scope.channelId) !== 0 &&
        scope.routeName === "conversation" &&
        scope.visible &&
        scope.online &&
        scope.pageActive
    );
    scope.fingerprint = JSON.stringify([
      scope.userId,
      scope.channelId,
      scope.conversation,
      scope.conversationEnd,
      scope.queue,
      scope.historyLoading,
      scope.socketAuthenticated,
      scope.sessionToken,
      scope.routeName,
      scope.visible,
      scope.online,
      scope.pageActive,
      scope.paused
    ]);
    return scope;
  };

  const getCoverageIdentity = scope =>
    JSON.stringify([scope.userId, scope.channelId, scope.sessionToken]);

  const clearCoverage = () => {
    coverageIdentity = undefined;
    coverageFloorMessages = undefined;
    coverageMode = undefined;
    provisionalCandidateFloorMessages = undefined;
    provisionalCandidateIdentity = undefined;
    provisionalCandidateObservedAt = undefined;
    coverageExpansionPending = false;
  };

  const clearScanContinuation = () => {
    scanContinuation = undefined;
    scanContinuationIdentity = undefined;
  };

  const clearFreshVerification = () => {
    freshVerificationPending = false;
  };

  const rotateGeneration = () => {
    const storedGeneration = getHistoryGeneration(store && store.state);
    generation = Math.max(generation + 1, storedGeneration + 1);
    if (store) {
      store.commit("ws/setHistoryGeneration", generation);
    }
    return generation;
  };

  const invalidateActiveWork = () => {
    workEpoch += 1;
    failureIndex = 0;
    const invalidatedRun = activeRun;
    // Detach first so a cancellation callback that never settles cannot keep
    // a restarted manager chained to stale work. Epoch and generation guards
    // still prevent the detached run from committing or scheduling timers.
    activeRun = undefined;
    if (invalidatedRun && typeof invalidatedRun.cancel === "function") {
      const cancel = invalidatedRun.cancel;
      invalidatedRun.cancel = undefined;
      try {
        cancel();
      } catch (error) {
        // Generation checks still prevent stale commits when cancellation fails.
      }
    }
  };

  const jitteredPollDelay = () => {
    const ratio = Number(timing.POLL_JITTER_RATIO) || 0;
    const offset = (Number(random()) * 2 - 1) * ratio;
    return Math.max(0, Math.round(timing.POLL_BASE_MS * (1 + offset)));
  };

  const canRunReason = () => readScope().eligible;

  let trigger;

  const scheduleCycle = (delay, reason) => {
    clearCycleTimer();
    if (!started || paused) return;
    cycleTimer = setTimer(() => {
      cycleTimer = undefined;
      trigger(reason);
    }, delay);
  };

  const schedulePoll = () => {
    if (readScope().eligible) {
      scheduleCycle(jitteredPollDelay(), HISTORY_RECONCILE_REASON.POLL);
    }
  };

  const refreshScope = ({ immediate = false } = {}) => {
    if (!store) return readScope();
    const nextScope = readScope();
    if (lastScope && lastScope.fingerprint === nextScope.fingerprint) {
      return nextScope;
    }

    const previousScope = lastScope;
    lastScope = nextScope;
    if (
      previousScope &&
      (getCoverageIdentity(previousScope) !== getCoverageIdentity(nextScope) ||
        !nextScope.conversation ||
        nextScope.conversationEnd)
    ) {
      clearCoverage();
    }
    // A scope/lifecycle transition invalidates the saved cursor. Resumption
    // starts at beginTime=0 so top-page changes are never hidden by old work.
    clearContinuationCooldown();
    clearScanContinuation();
    clearFreshVerification();
    invalidateActiveWork();
    rotateGeneration();
    clearCycleTimer();
    clearDelayedTimer();
    clearFailureCooldown();

    if (nextScope.eligible) {
      const changedActiveIdentity =
        previousScope &&
        previousScope.eligible &&
        (previousScope.userId !== nextScope.userId ||
          previousScope.channelId !== nextScope.channelId);
      const changedSessionToken =
        previousScope &&
        previousScope.eligible &&
        previousScope.sessionToken !== nextScope.sessionToken;
      const pending = pendingReason;
      pendingReason = undefined;
      const becameEligible = previousScope && !previousScope.eligible;
      const resumedAfterHistory =
        becameEligible && previousScope.historyLoading === true;
      const reconnectAfterHistory = Boolean(
        resumedAfterHistory && nextScope.reconnecting && !pending
      );
      const initialHistorySettlement = Boolean(
        resumedAfterHistory &&
          coverageIdentity !== getCoverageIdentity(nextScope) &&
          !nextScope.reconnecting &&
          !pending
      );
      if (resumedAfterHistory) coverageExpansionPending = true;
      if (
        pending ||
        immediate ||
        changedActiveIdentity ||
        changedSessionToken ||
        becameEligible
      ) {
        const reason =
          pending ||
          (reconnectAfterHistory
            ? HISTORY_RECONCILE_REASON.RECONNECT
            : initialHistorySettlement
            ? HISTORY_RECONCILE_REASON.INITIAL_HISTORY
            : becameEligible && !previousScope.socketAuthenticated
            ? HISTORY_RECONCILE_REASON.AUTHENTICATED
            : HISTORY_RECONCILE_REASON.SCOPE);
        // Once ordinary initial history settles, use one persistence-window
        // verification instead of duplicating immediate and delayed scans.
        // Reconnect and lifecycle recovery reasons still run immediately.
        const delay = initialHistorySettlement
          ? Number(timing.DELAYED_SECOND_MS) ||
            HISTORY_RECONCILE_TIMING.DELAYED_SECOND_MS
          : resumedAfterHistory
          ? Number(timing.HISTORY_SETTLE_MS) ||
            HISTORY_RECONCILE_TIMING.HISTORY_SETTLE_MS
          : 0;
        scheduleCycle(delay, reason);
      } else {
        schedulePoll();
      }
    }
    return nextScope;
  };

  const emitDiagnostic = (event, diagnosticSink = diagnostics) => {
    try {
      if (typeof diagnosticSink === "function") diagnosticSink(event);
    } catch (error) {
      // Diagnostics must never disable message recovery.
    }
  };

  const scheduleDelayedSecond = (
    reason,
    runGeneration,
    runEpoch,
    force = false
  ) => {
    if ((!force && !isResumeReason(reason)) || delayedTimer !== undefined) {
      return;
    }
    delayedTimer = setTimer(() => {
      delayedTimer = undefined;
      if (
        workEpoch !== runEpoch ||
        generation !== runGeneration ||
        !readScope().eligible
      ) {
        return;
      }
      trigger(HISTORY_RECONCILE_REASON.DELAYED);
    }, timing.DELAYED_SECOND_MS);
  };

  const scheduleContinuationBackoff = (delay, reason) => {
    if (continuationCooldownTimer !== undefined) {
      clearTimer(continuationCooldownTimer);
    }
    continuationCoolingDown = true;
    continuationPendingReason = chooseReason(continuationPendingReason, reason);
    continuationCooldownTimer = setTimer(() => {
      continuationCooldownTimer = undefined;
      continuationCoolingDown = false;
      const resumeReason =
        continuationPendingReason || HISTORY_RECONCILE_REASON.GAP;
      continuationPendingReason = undefined;
      if (started && !paused) trigger(resumeReason);
    }, delay);
  };

  const scheduleFailureBackoff = (delay, reason) => {
    clearCycleTimer();
    failureCoolingDown = true;
    failurePendingReason = undefined;
    cycleTimer = setTimer(() => {
      cycleTimer = undefined;
      failureCoolingDown = false;
      const retryReason = chooseReason(reason, failurePendingReason);
      failurePendingReason = undefined;
      if (started && !paused) trigger(retryReason);
    }, delay);
  };

  const execute = async (reason, runState) => {
    const runEpoch = runState.epoch;
    const runStore = store;
    const runGetHistoryMessage = getHistoryMessage;
    const runCreateRequestCancellation = createRequestCancellation;
    const runDiagnostics = diagnostics;
    const runGeneration = generation;
    const runScope = readScope();
    const runCoverageIdentity = getCoverageIdentity(runScope);
    const savedScanContinuation =
      scanContinuationIdentity === runCoverageIdentity
        ? scanContinuation
        : undefined;
    const runScanContinuation = savedScanContinuation;
    const runIsFreshVerification = Boolean(
      (runScanContinuation && runScanContinuation.freshVerification) ||
        freshVerificationPending
    );
    const runCoverageFloorMessages =
      coverageIdentity === runCoverageIdentity
        ? coverageFloorMessages
        : undefined;
    const runCoverageMode =
      coverageIdentity === runCoverageIdentity ? coverageMode : undefined;
    const runProvisionalCandidateIdentity =
      coverageIdentity === runCoverageIdentity
        ? provisionalCandidateIdentity
        : undefined;
    const runProvisionalCandidateFloorMessages =
      coverageIdentity === runCoverageIdentity
        ? provisionalCandidateFloorMessages
        : undefined;
    const runProvisionalCandidateObservedAt =
      coverageIdentity === runCoverageIdentity
        ? provisionalCandidateObservedAt
        : undefined;
    const runCoverageExpansionPending = coverageExpansionPending;
    const startedAt = now();
    let requestCancellation;
    let result;

    try {
      if (typeof runCreateRequestCancellation === "function") {
        requestCancellation = runCreateRequestCancellation();
        runState.cancel =
          requestCancellation &&
          typeof requestCancellation.cancel === "function"
            ? requestCancellation.cancel
            : undefined;
      }
    } catch (error) {
      requestCancellation = undefined;
      runState.cancel = undefined;
    }

    const releaseCancellation = () => {
      if (
        requestCancellation &&
        runState.cancel === requestCancellation.cancel
      ) {
        runState.cancel = undefined;
      }
    };

    const runReconcile = ({
      continuation,
      pageLimit,
      establishCoverage,
      floorMessages = runCoverageFloorMessages,
      lockCoverageFloor = false
    }) =>
      reconcileHistoryOnce({
        state: runStore.state,
        commit: (type, payload, options) =>
          runStore.commit(type, payload, options),
        getHistoryMessage: runGetHistoryMessage,
        generation: runGeneration,
        anchorMessages: floorMessages,
        coverageMode: runCoverageMode,
        allowCoverageExpansion: runCoverageExpansionPending,
        lockCoverageFloor,
        continuation,
        maxPages: pageLimit,
        maxDurationMs,
        provisionalWindowMessages:
          Number(timing.HEAD_PROBE_WINDOW_MESSAGES) ||
          HISTORY_RECONCILE_TIMING.HEAD_PROBE_WINDOW_MESSAGES,
        now,
        requestOptions:
          requestCancellation && requestCancellation.requestOptions,
        retryDelays,
        waitForRetry,
        onCoverageEstablished: establishCoverage
          ? (messages, mode) => {
              if (
                coverageIdentity !== runCoverageIdentity &&
                workEpoch === runEpoch &&
                store === runStore &&
                started &&
                !paused
              ) {
                coverageIdentity = runCoverageIdentity;
                coverageFloorMessages = messages;
                coverageMode = mode;
                provisionalCandidateFloorMessages = undefined;
                provisionalCandidateIdentity = undefined;
                provisionalCandidateObservedAt = undefined;
              }
            }
          : undefined
      });

    const previousHeadCheckedAt = toFiniteNumber(
      runScanContinuation && runScanContinuation.headCheckedAt
    );
    const headProbeInterval =
      Number(timing.HEAD_PROBE_INTERVAL_MS) ||
      HISTORY_RECONCILE_TIMING.HEAD_PROBE_INTERVAL_MS;
    const headProbeWindowSize = Math.max(
      1,
      Number(timing.HEAD_PROBE_WINDOW_MESSAGES) ||
        HISTORY_RECONCILE_TIMING.HEAD_PROBE_WINDOW_MESSAGES
    );
    const headProbeMaxPages = Math.max(
      1,
      Math.min(
        Number(maxPages) || DEFAULT_MAX_PAGES,
        Number(timing.HEAD_PROBE_MAX_PAGES) ||
          HISTORY_RECONCILE_TIMING.HEAD_PROBE_MAX_PAGES
      )
    );
    const shouldProbeHead = Boolean(
      runScanContinuation &&
        (isImmediateHeadProbeReason(reason) ||
          previousHeadCheckedAt === undefined ||
          startedAt < previousHeadCheckedAt ||
          startedAt - previousHeadCheckedAt >= headProbeInterval)
    );
    let headCheckedAt = previousHeadCheckedAt;
    let headProbeResult;

    try {
      if (shouldProbeHead) {
        const headCoverageFloor = selectHeadCoverageFloor(
          getMessagesForUser(runStore.state, runScope.userId),
          runScope.channelId,
          headProbeWindowSize
        );
        headProbeResult = await runReconcile({
          continuation: undefined,
          pageLimit: headProbeMaxPages,
          establishCoverage: false,
          floorMessages: headCoverageFloor,
          lockCoverageFloor: true
        });
        headCheckedAt = now();
      }

      if (
        headProbeResult &&
        !["partial", "synced"].includes(headProbeResult.status)
      ) {
        result = headProbeResult;
      } else {
        result = await runReconcile({
          continuation: runScanContinuation,
          pageLimit: maxPages,
          establishCoverage: true
        });
      }
    } catch (error) {
      releaseCancellation();
      emitDiagnostic(
        {
          reason,
          generation: runGeneration,
          pagesFetched: 0,
          candidateCount: 0,
          mergedCount: 0,
          dedupedCount: 0,
          gapCount: 0,
          collisionCount: 0,
          keylessCount: 0,
          malformedCount: 0,
          staleDiscardCount: 0,
          duration: Math.max(0, now() - startedAt),
          result: "failed"
        },
        runDiagnostics
      );
      return { status: "failed", error };
    }
    releaseCancellation();

    if (
      headProbeResult &&
      ["partial", "synced"].includes(headProbeResult.status) &&
      result !== headProbeResult
    ) {
      [
        "addedCount",
        "pageCount",
        "candidateCount",
        "dedupedCount",
        "collisionCount",
        "keylessCount",
        "malformedCount",
        "gapCount",
        "staleDiscardCount"
      ].forEach(field => {
        result[field] =
          Number(result[field] || 0) + Number(headProbeResult[field] || 0);
      });
    }

    if (
      result.status === "synced" &&
      result.coverageMode === HISTORY_COVERAGE_MODE.PROVISIONAL
    ) {
      // A bootstrap candidate may only stay fixed or move older, and must be
      // observed again after the persistence-delay window. Both observations
      // are terminal scans, so deep gaps are covered before steady polling.
      const candidateMessages = selectOlderCoverageCandidate(
        runProvisionalCandidateFloorMessages,
        result.provisionalCandidateFloorMessages
      );
      const candidateIdentity = createCoverageCandidateIdentity(
        candidateMessages
      );
      const candidateChanged =
        candidateIdentity !== runProvisionalCandidateIdentity;
      const observedAt = candidateChanged
        ? now()
        : runProvisionalCandidateObservedAt;
      const verificationDelay =
        Number(timing.DELAYED_SECOND_MS) ||
        HISTORY_RECONCILE_TIMING.DELAYED_SECOND_MS;
      const verificationDue =
        observedAt !== undefined && now() - observedAt >= verificationDelay;

      if (
        candidateMessages.length > 0 &&
        !candidateChanged &&
        verificationDue
      ) {
        result.coverageMode = HISTORY_COVERAGE_MODE.FINITE;
        result.coverageFloorMessages = candidateMessages.map(message => ({
          ...message
        }));
        result.provisionalCandidateIdentity = undefined;
        result.provisionalCandidateObservedAt = undefined;
        result.provisionalCandidateFloorMessages = undefined;
        result.needsCoverageVerification = false;
      } else {
        result.provisionalCandidateFloorMessages = candidateMessages.map(
          message => ({ ...message })
        );
        result.provisionalCandidateIdentity = candidateIdentity;
        result.provisionalCandidateObservedAt = observedAt;
        result.needsCoverageVerification =
          candidateChanged ||
          runProvisionalCandidateIdentity === undefined ||
          (candidateMessages.length > 0 && !verificationDue);
      }
    }

    const isCurrentRun =
      workEpoch === runEpoch && store === runStore && started && !paused;

    if (isCurrentRun && result.status === "synced") {
      coverageIdentity = runCoverageIdentity;
      coverageFloorMessages = Array.isArray(result.coverageFloorMessages)
        ? result.coverageFloorMessages.map(message => ({ ...message }))
        : [];
      coverageMode = result.coverageMode;
      if (coverageMode === HISTORY_COVERAGE_MODE.PROVISIONAL) {
        provisionalCandidateFloorMessages = Array.isArray(
          result.provisionalCandidateFloorMessages
        )
          ? result.provisionalCandidateFloorMessages.map(message => ({
              ...message
            }))
          : [];
        provisionalCandidateIdentity = result.provisionalCandidateIdentity;
        provisionalCandidateObservedAt = result.provisionalCandidateObservedAt;
      } else {
        provisionalCandidateFloorMessages = undefined;
        provisionalCandidateIdentity = undefined;
        provisionalCandidateObservedAt = undefined;
      }
      coverageExpansionPending = false;
    }

    if (isCurrentRun && result.status === "partial") {
      const burstPages =
        Number((runScanContinuation && runScanContinuation.burstPages) || 0) +
        Number(result.pageCount || 0);
      const burstLimit = Math.max(
        1,
        Number(maxBurstPages) || DEFAULT_MAX_BURST_PAGES
      );
      const continuationBackoff = burstPages >= burstLimit;
      scanContinuationIdentity = runCoverageIdentity;
      scanContinuation = {
        ...result.continuation,
        originReason:
          (runScanContinuation && runScanContinuation.originReason) || reason,
        gapObserved: Boolean(
          (runScanContinuation && runScanContinuation.gapObserved) ||
            result.gapCount > 0 ||
            result.addedCount > 0
        ),
        burstPages: continuationBackoff ? 0 : burstPages,
        // A burst backoff is also a freshness checkpoint. Clear the timestamp
        // so the next resume reconciles beginTime=0 before continuing from the
        // preserved deep cursor.
        headCheckedAt: continuationBackoff
          ? undefined
          : headCheckedAt === undefined
          ? startedAt
          : headCheckedAt,
        freshVerification: runIsFreshVerification
      };
      freshVerificationPending = false;
      result.continuationBackoff = continuationBackoff;
    } else if (isCurrentRun && result.status === "synced") {
      result.originReason =
        (runScanContinuation && runScanContinuation.originReason) || reason;
      result.gapObserved = Boolean(
        (runScanContinuation && runScanContinuation.gapObserved) ||
          result.gapCount > 0 ||
          result.addedCount > 0
      );
      result.needsFreshPass =
        Boolean(runScanContinuation) &&
        !runIsFreshVerification &&
        result.coverageMode !== HISTORY_COVERAGE_MODE.PROVISIONAL;
      if (result.needsFreshPass) freshVerificationPending = true;
      if (runIsFreshVerification) freshVerificationPending = false;
      clearScanContinuation();
    }

    if (
      isCurrentRun &&
      result.status === "synced" &&
      runtimeAuthenticated &&
      runStore.state.ws &&
      runStore.state.ws.isReconnect === true
    ) {
      runStore.commit("ws/setIsReconnect", false);
    }

    emitDiagnostic(
      {
        reason,
        generation: runGeneration,
        pagesFetched: result.pageCount || 0,
        candidateCount: result.candidateCount || 0,
        mergedCount: result.addedCount || 0,
        dedupedCount: result.dedupedCount || 0,
        gapCount: result.gapCount || 0,
        collisionCount: result.collisionCount || 0,
        keylessCount: result.keylessCount || 0,
        malformedCount: result.malformedCount || 0,
        staleDiscardCount: result.staleDiscardCount || 0,
        duration: Math.max(0, now() - startedAt),
        result: result.status
      },
      runDiagnostics
    );

    if (isCurrentRun && result.status === "synced") {
      scheduleDelayedSecond(
        result.originReason || reason,
        runGeneration,
        runEpoch
      );
      if (result.gapObserved) {
        scheduleDelayedSecond(
          HISTORY_RECONCILE_REASON.GAP,
          runGeneration,
          runEpoch
        );
      }
      if (result.needsCoverageVerification) {
        scheduleDelayedSecond(reason, runGeneration, runEpoch, true);
      }
    }
    return result;
  };

  const finishDrain = (result, reason) => {
    if (!readScope().eligible) {
      clearCycleTimer();
      return;
    }

    if (result && result.status === "failed") {
      const delays =
        Array.isArray(timing.FAILURE_BACKOFF_MS) &&
        timing.FAILURE_BACKOFF_MS.length > 0
          ? timing.FAILURE_BACKOFF_MS
          : HISTORY_RECONCILE_TIMING.FAILURE_BACKOFF_MS;
      const delay = delays[Math.min(failureIndex, delays.length - 1)];
      failureIndex = Math.min(failureIndex + 1, delays.length - 1);
      scheduleFailureBackoff(delay, reason);
      return;
    }

    failureIndex = 0;
    clearFailureCooldown();
    if (result && result.status === "partial") {
      const reason = result.resumeReason || HISTORY_RECONCILE_REASON.GAP;
      if (result.continuationBackoff) {
        scheduleContinuationBackoff(
          Number(timing.CONTINUATION_BACKOFF_MS) ||
            HISTORY_RECONCILE_TIMING.CONTINUATION_BACKOFF_MS,
          reason
        );
      } else {
        scheduleCycle(
          Number(timing.CONTINUATION_DELAY_MS) ||
            HISTORY_RECONCILE_TIMING.CONTINUATION_DELAY_MS,
          reason
        );
      }
      return;
    }
    if (result && result.needsFreshPass) {
      scheduleCycle(0, HISTORY_RECONCILE_REASON.VERIFY);
      return;
    }
    schedulePoll();
  };

  const drain = async (firstReason, runState) => {
    runState.acceptsTrailing = true;
    let result;
    let finalReason = firstReason;

    try {
      result = await execute(firstReason, runState);
      const nextReason = runState.restartReason || runState.trailingReason;
      runState.restartReason = undefined;
      runState.trailingReason = undefined;
      runState.acceptsTrailing = false;

      const mustYieldForBurst = Boolean(
        result && result.status === "partial" && result.continuationBackoff
      );
      if (nextReason && mustYieldForBurst) {
        result.resumeReason = chooseReason(
          HISTORY_RECONCILE_REASON.GAP,
          nextReason
        );
      } else if (nextReason && canRunReason(nextReason)) {
        runState.epoch = workEpoch;
        result = await execute(nextReason, runState);
        finalReason = nextReason;
      }
    } catch (error) {
      result = { status: "failed", error };
    } finally {
      if (activeRun === runState) {
        const restartReason = runState.restartReason;
        activeRun = undefined;
        if (
          restartReason &&
          started &&
          !paused &&
          canRunReason(restartReason)
        ) {
          scheduleCycle(0, restartReason);
        } else if (workEpoch === runState.epoch) {
          try {
            finishDrain(result, finalReason);
          } catch (error) {
            emitDiagnostic({
              reason: finalReason,
              generation,
              pagesFetched: 0,
              candidateCount: 0,
              mergedCount: 0,
              dedupedCount: 0,
              gapCount: 0,
              collisionCount: 0,
              keylessCount: 0,
              malformedCount: 0,
              staleDiscardCount: 0,
              duration: 0,
              result: "failed"
            });
            scheduleFailureBackoff(
              HISTORY_RECONCILE_TIMING.FAILURE_BACKOFF_MS[0],
              finalReason
            );
          }
        }
      }
    }
    return result;
  };

  trigger = reason => {
    const normalizedReason = reason || HISTORY_RECONCILE_REASON.POLL;
    if (continuationCoolingDown) {
      continuationPendingReason = chooseReason(
        continuationPendingReason,
        normalizedReason
      );
      return Promise.resolve({
        status: "deferred",
        addedCount: 0,
        pageCount: 0
      });
    }
    if (failureCoolingDown) {
      failurePendingReason = chooseReason(
        failurePendingReason,
        normalizedReason
      );
      return Promise.resolve({
        status: "deferred",
        addedCount: 0,
        pageCount: 0
      });
    }
    if (!started || paused || !canRunReason(normalizedReason)) {
      if (isResumeReason(normalizedReason) && started && !paused) {
        pendingReason = chooseReason(pendingReason, normalizedReason);
      }
      return Promise.resolve({
        status: "skipped",
        addedCount: 0,
        pageCount: 0
      });
    }

    clearCycleTimer();
    if (activeRun) {
      if (activeRun.epoch !== workEpoch) {
        activeRun.restartReason = chooseReason(
          activeRun.restartReason,
          normalizedReason
        );
      } else if (activeRun.acceptsTrailing) {
        activeRun.trailingReason = chooseReason(
          activeRun.trailingReason,
          normalizedReason
        );
      }
      return activeRun.promise;
    }

    const runState = {
      epoch: workEpoch,
      acceptsTrailing: false,
      trailingReason: undefined,
      restartReason: undefined,
      cancel: undefined,
      promise: undefined
    };
    runState.promise = drain(normalizedReason, runState);
    activeRun = runState;
    return runState.promise;
  };

  const handleOnline = () => {
    refreshScope();
    trigger(HISTORY_RECONCILE_REASON.ONLINE);
  };

  const handleOffline = () => {
    refreshScope();
  };

  const handleVisibility = () => {
    const visible = isVisible();
    refreshScope();
    if (visible) trigger(HISTORY_RECONCILE_REASON.VISIBLE);
  };

  const handleFocus = () => {
    refreshScope();
    trigger(HISTORY_RECONCILE_REASON.FOCUS);
  };

  const handlePageShow = () => {
    pageActive = true;
    refreshScope();
    trigger(HISTORY_RECONCILE_REASON.PAGESHOW);
  };

  const handlePageHide = () => {
    pageActive = false;
    refreshScope();
  };

  const addListeners = () => {
    if (listenersAttached) return;
    if (windowObject) {
      windowObject.addEventListener("online", handleOnline);
      windowObject.addEventListener("offline", handleOffline);
      windowObject.addEventListener("focus", handleFocus);
      windowObject.addEventListener("pageshow", handlePageShow);
      windowObject.addEventListener("pagehide", handlePageHide);
    }
    if (documentObject) {
      documentObject.addEventListener("visibilitychange", handleVisibility);
    }
    listenersAttached = true;
  };

  const removeListeners = () => {
    if (!listenersAttached) return;
    if (windowObject) {
      windowObject.removeEventListener("online", handleOnline);
      windowObject.removeEventListener("offline", handleOffline);
      windowObject.removeEventListener("focus", handleFocus);
      windowObject.removeEventListener("pageshow", handlePageShow);
      windowObject.removeEventListener("pagehide", handlePageHide);
    }
    if (documentObject) {
      documentObject.removeEventListener("visibilitychange", handleVisibility);
    }
    listenersAttached = false;
  };

  const stop = () => {
    if (!started || paused) return;
    paused = true;
    clearCycleTimer();
    clearDelayedTimer();
    clearContinuationCooldown();
    clearFailureCooldown();
    refreshScope();
  };

  const destroy = () => {
    if (store) rotateGeneration();
    invalidateActiveWork();
    clearCycleTimer();
    clearDelayedTimer();
    clearContinuationCooldown();
    clearFailureCooldown();
    removeListeners();
    if (typeof unsubscribeStore === "function") unsubscribeStore();
    if (typeof removeRouterHook === "function") removeRouterHook();
    if (
      lifecycleVm &&
      typeof lifecycleVm.$off === "function" &&
      lifecycleDestroyHandler
    ) {
      lifecycleVm.$off("hook:beforeDestroy", lifecycleDestroyHandler);
    }
    unsubscribeStore = undefined;
    removeRouterHook = undefined;
    lifecycleVm = undefined;
    lifecycleDestroyHandler = undefined;
    clearCoverage();
    clearScanContinuation();
    clearFreshVerification();
    started = false;
    paused = false;
    runtimeAuthenticated = false;
    pageActive = true;
    lastScope = undefined;
    pendingReason = undefined;
    store = undefined;
    router = undefined;
    getHistoryMessage = undefined;
    createRequestCancellation = undefined;
    diagnostics = undefined;
  };

  const start = options => {
    if (
      !options ||
      !options.store ||
      typeof options.getHistoryMessage !== "function"
    ) {
      throw new TypeError("store and getHistoryMessage are required");
    }

    const nextStore = options.store;
    const nextRouter =
      started && options.router === undefined ? router : options.router;
    const nextLifecycleVm =
      started && options.vm === undefined ? lifecycleVm : options.vm;
    const shouldRestartLifecycle = Boolean(
      started &&
        (store !== nextStore ||
          router !== nextRouter ||
          lifecycleVm !== nextLifecycleVm)
    );
    const preserveAuthentication = Boolean(
      shouldRestartLifecycle && store === nextStore && runtimeAuthenticated
    );
    if (shouldRestartLifecycle) destroy();

    store = nextStore;
    router = nextRouter;
    getHistoryMessage = options.getHistoryMessage;
    createRequestCancellation = options.createRequestCancellation;
    diagnostics = options.diagnostics;
    if (preserveAuthentication) runtimeAuthenticated = true;

    // Re-registering the same owner is idempotent. In particular it must not
    // replace a pending failure/continuation cooldown with an immediate timer.
    if (started && !paused) return generation;

    if (!started) {
      invalidateActiveWork();
      started = true;
      paused = false;
      pageActive = true;
      rotateGeneration();
      lastScope = readScope();
      addListeners();
      if (typeof store.subscribe === "function") {
        unsubscribeStore = store.subscribe(() => refreshScope());
      }
      if (router && typeof router.afterEach === "function") {
        removeRouterHook = router.afterEach(() => refreshScope());
      }
      if (nextLifecycleVm && typeof nextLifecycleVm.$once === "function") {
        lifecycleVm = nextLifecycleVm;
        lifecycleDestroyHandler = destroy;
        lifecycleVm.$once("hook:beforeDestroy", lifecycleDestroyHandler);
      }
    } else if (paused) {
      paused = false;
      refreshScope({ immediate: true });
    }

    if (readScope().eligible) {
      const initialReason =
        store.state.ws && store.state.ws.isReconnect === true
          ? HISTORY_RECONCILE_REASON.RECONNECT
          : HISTORY_RECONCILE_REASON.AUTHENTICATED;
      scheduleCycle(0, initialReason);
    }
    return generation;
  };

  const setSocketAuthenticated = authenticated => {
    const nextValue = authenticated === true;
    if (runtimeAuthenticated === nextValue) return generation;
    runtimeAuthenticated = nextValue;
    refreshScope();
    return generation;
  };

  return {
    start,
    stop,
    destroy,
    setSocketAuthenticated,
    trigger,
    getGeneration: () => generation,
    isRunning: () => started && !paused
  };
};

export const historyReconcileManager = createHistoryReconcileManager();
